import {
  createColumn,
  formatAggregatedFooter,
  formatNumericCell,
  formatPriceCell,
  getCommonBodyCellProps,
  getCommonFooterCellProps,
  getCommonHeadCellProps,
} from "./commonUtils";
import { formatCurrency, formatNumber } from "../../../utils/dataCalculations";

const noMaxWidth = { maxWidth: "none" };

export const getEcdColumns = (theme, globalFilter) => {
  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  const highlightMatch = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    const textStr = String(text);
    const searchStr = String(searchTerm);
    const regex = new RegExp(`(${searchStr})`, "gi");
    const parts = textStr.split(regex);
    return parts?.map((part, index) =>
      part.toLowerCase() === searchStr.toLowerCase() ? (
        <span
          key={index}
          style={{
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.contrastText,
            padding: "1px 2px",
            borderRadius: theme.shape.borderRadius * 0.5,
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "closing_date",
      "Closing Date",
      85,
      "left",
      {
        head: {
          ...noMaxWidth,
        },
        body: {
          ...noMaxWidth,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        footer: noMaxWidth,
        other: {
          enableGrouping: true,
          Cell: ({ cell }) =>
            highlightMatch(cell.getValue() || "-", globalFilter),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyType",
      "",
      120,
      "left",
      {
        head: noMaxWidth,
        body: {
          ...noMaxWidth,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        footer: noMaxWidth,
        other: {
          enableGrouping: true,
          Cell: ({ cell }) =>
            highlightMatch(cell.getValue() || "", globalFilter),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyName",
      "",
      120,
      "left",
      {
        head: noMaxWidth,
        body: {
          ...noMaxWidth,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        footer: noMaxWidth,
        other: {
          enableGrouping: true,
          Cell: ({ cell }) =>
            highlightMatch(cell.getValue() || "", globalFilter),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "security_name",
      "Security",
      140,
      "left",
      {
        head: noMaxWidth,
        body: {
          ...noMaxWidth,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        footer: noMaxWidth,
        other: {
          grow: true,
          Cell: ({ cell }) =>
            highlightMatch(cell.getValue() || "-", globalFilter),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "realtime_quantity",
      "Qty",
      75,
      "right",
      {
        head: noMaxWidth,
        body: noMaxWidth,
        footer: noMaxWidth,
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_price",
      "Price",
      70,
      "right",
      {
        head: noMaxWidth,
        body: noMaxWidth,
        footer: noMaxWidth,
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "last_closing_price",
      "Last Close",
      80,
      "right",
      {
        head: noMaxWidth,
        body: noMaxWidth,
        footer: noMaxWidth,
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "LMV",
      "LMV",
      90,
      "right",
      {
        head: noMaxWidth,
        body: noMaxWidth,
        footer: noMaxWidth,
        other: {
          enableGrouping: false,
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.text.primary,
              theme.palette.text.primary,
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.text.primary,
              theme.palette.text.primary,
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "LMV",
              formatCurrency,
              theme.palette.text.primary,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "SMV",
      "SMV",
      90,
      "right",
      {
        head: noMaxWidth,
        body: noMaxWidth,
        footer: noMaxWidth,
        other: {
          enableGrouping: true,
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "SMV",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.text.primary,
            ),
        },
      },
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cumulative_lmv",
      "Cumul.",
      90,
      "right",
      {
        head: noMaxWidth,
        body: noMaxWidth,
        footer: noMaxWidth,
        other: {
          enableGrouping: false,
          aggregationFn: (_columnId, leafRows) => {
            if (!leafRows.length) return null;
            return leafRows[0].original.cumulative_lmv ?? null;
          },
          Cell: () => null,
          AggregatedCell: ({ row, cell }) => {
            if (row.depth !== 0) return null;
            return formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.text.primary,
              theme.palette.text.primary,
            );
          },
          Footer: () => null,
        },
      },
    ),
  ];
};
